import { FormControlLabel, Switch, Grid, TextField, FormLabel, Button, InputAdornment } from '@material-ui/core'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, LayersControl, GeoJSON } from 'react-leaflet'
import { usePosition } from '../hooks/usePosition'
import { graden2amersfoort, geometry2geoJson } from '../utils/location'
import marker from '../assets/icons/marker-icon.png'
import markerShadow from '../assets/icons/marker-shadow.png'
import { coloredMarker } from '../utils/marker'
import useOnlineStatus from '../hooks/useOnlineStatus';

import * as L from "leaflet";

/*
ToDo: observations bevat andere waarnemingen binnen hetzelfde gebied. Als gebruiker een dergelijke waarneming selecteert,
worden de gegevens overgenomen.
*/

const Location = ({ form,
    field,
    readonly,
    onChange,
    plot,
    observations,
    onSelect,
    errorProps,
    ...rest }) => {

    const [map, setMap] = useState(null); // init map
    const [pos, setPos] = useState(''); // coördinaten voor textfield
    const [buffer, setBuffer] = useState(field.value && field.value.buffer); // buffer voor textfield
    const [gps, setGPS] = useState(field.value == null && form.values.observation.id < 0 ? true : false); // gps aan of uit
    const [selectedPosition, setSelectedPosition] = useState([]) // Locatie marker en center
    const markerRef = useRef(null) // init marker
    const formRef = useRef(form)
    const isOnline = useOnlineStatus()


    var blueIcon = new L.Icon({
        iconUrl: marker,
        shadowUrl: markerShadow,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
    });

    const watch = true;
    const { latitude, longitude, accuracy } = usePosition(watch);

    const save = useCallback((lat, lon, buffer) => {
        if (lat && lon) {
            formRef.current.setFieldValue(field.name, { geometry: 'POINT(' + lon + ' ' + lat + ')', srid: 4326, buffer: Math.round(buffer, 0) })
            formRef.current.setFieldTouched(field.name)
        } else {
            formRef.current.setFieldValue(field.name, null)
            formRef.current.setFieldTouched(field.name)
        }
    }, [field.name, formRef])

    useEffect(() => {
        // Wanneer er al een locatie bij de waarneming aanwezig is, deze tonen
        if (field.value && field.value.geometry) {
            var loc = geometry2geoJson(field.value.geometry).coordinates;
            setPos([graden2amersfoort(loc[1], loc[0])])
            setSelectedPosition([loc[1], loc[0]])
            if (map) {
                map.setView([loc[1], loc[0]], 17)
            }
        }
    }, [map, field.value])

    useEffect(() => {
        // GPS locatie tonen op kaart en tonen coördinaten
        if (latitude && longitude && gps === true) {
            setSelectedPosition([latitude, longitude])
            setPos([graden2amersfoort(latitude, longitude)])
            setBuffer(accuracy)
            save(latitude, longitude, accuracy)
            if (map.getZoom() < 8 && accuracy < 100) {
                map.setView([latitude, longitude], 15)
            }
        }
    }, [latitude, longitude, accuracy, map, gps, save])

    const toggleGPS = (gps) => {
        // GPS toggelen
        setGPS(gps)
        if (!gps) {
            form.setFieldValue(field.name, '')
            setPos('')
            setBuffer('')
        }
    }
    const removeLocation = () => {
        // Locatie verwijderen
        setGPS(false)
        setPos('')
        setBuffer('')
        setSelectedPosition([])
        save('', '', '')
    }


    const zoombuffer = [
        { "zoom": 9, "buffer": 2000 },
        { "zoom": 10, "buffer": 1000 },
        { "zoom": 11, "buffer": 500 },
        { "zoom": 12, "buffer": 100 },
        { "zoom": 13, "buffer": 50 },
        { "zoom": 14, "buffer": 25 },
        { "zoom": 15, "buffer": 10 },
        { "zoom": 16, "buffer": 5 },
        { "zoom": 17, "buffer": 3 },
        { "zoom": 18, "buffer": 1 },
        { "zoom": 19, "buffer": 1 },
        { "zoom": 20, "buffer": 1 }
    ]

    const Markers = () => {
        useMapEvents({
            click(e) {
                if (!readonly) {
                    setSelectedPosition([
                        e.latlng.lat,
                        e.latlng.lng
                    ]);
                    setPos([graden2amersfoort(e.latlng.lat, e.latlng.lng)])
                    setGPS(false)
                    const zoom = zoombuffer.filter(item => item.zoom === e.target._zoom);
                    if (zoom.length > 0) {
                        save(e.latlng.lat, e.latlng.lng, 10)
                        setBuffer(10)
                    } else {
                        save(e.latlng.lat, e.latlng.lng, 5000)
                        setBuffer(5000)
                    }
                }
            },
        })

        return (
            selectedPosition.length > 0 ?
                <Marker
                    icon={blueIcon}
                    ref={markerRef}
                    key={selectedPosition[0]}
                    position={selectedPosition}
                    interactive={true}
                    draggable={false}
                />
                : null
        )
    }


    const Circles = () => {
        return (
            selectedPosition.length > 0 && buffer > 0 ?
                <Circle center={selectedPosition}
                    radius={buffer}
                />
                : null
        )
    }

    const Plots = () => {
        const arr = [geometry2geoJson(plot.location.geometry)]
        if ((map && arr) && map.getZoom() < 8) {
            // Indien er een geojson aanwezig is & er nog niet is ingezoom, dan zoomen. Voor dit laatste later een robustere oplossing verzinnen.
            // Bij een bestaande waarneming met een locatie is al ingezoomd (via een andere functie) op de locatie van de waarneming
            var geojson = L.geoJSON(arr)
            map.fitBounds(geojson.getBounds())
        }

        return ( 
            arr ?
                <GeoJSON data={arr} /> : null
        )
    }

    const Observations = () => {
        const feature = (observations) => {
            const feat = {
                type: 'Feature',
                properties: { ...observations },
                geometry: geometry2geoJson(observations.location.geometry)
            }
            return feat;
        }
        const geojson = observations.filter(o => o.location != null).map(o => feature(o))
        return (
            <GeoJSON data={geojson} pointToLayer={pointToLayer}/>
        )
    }

    const pointToLayer = (feature, latlng) => {
        var color = process.env.REACT_APP_PRIMARYCOLOR
        var marker
        marker = L.marker(latlng, {
            icon: coloredMarker({ L, color })
        })
        marker.on("click", (e) => onSelect(feature.properties))
        return marker
    }

    return (
        <>
            <Grid container>

                <Grid item xs={12}>
                    <FormLabel component="legend">Locatie</FormLabel>
                </Grid>

                <Grid item xs={6} sm={6}>
                    <TextField value={pos && pos} disabled fullWidth {...errorProps('observation.location.pos')} />
                </Grid>

                <Grid item xs={6} sm={6}>
                    <TextField value={buffer & buffer} fullWidth error={buffer > 50} disabled {...errorProps('observation.location.buffer')}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">±</InputAdornment>,
                            endAdornment: <InputAdornment position="end">m.</InputAdornment>
                        }}
                    />
                </Grid>

                <Grid item xs={4} sm={6}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={gps}
                                onChange={e => toggleGPS(e.target.checked)}
                                name="checkedB"
                                color="primary"
                                disabled={readonly}
                            />
                        }
                        label="GPS" />
                </Grid>

                <Grid item xs={8} sm={6}>
                    {!readonly &&
                        <Button color="primary" onClick={removeLocation}>Verwijder detaillocatie</Button>}
                </Grid>

            </Grid>

            <div>
                <MapContainer center={selectedPosition.length > 0 ? selectedPosition : [52.3, 5.4]} zoom={7} scrollWheelZoom={true} style={{ height: "30vh",display:isOnline ? 'block' : 'none'}} whenCreated={setMap} >
                    <LayersControl position="topright">
                        <LayersControl.BaseLayer checked name="OpenStreetMap">
                            <TileLayer
                                attribution='&copy; <a href="https://www..org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                        </LayersControl.BaseLayer>
                        <LayersControl.BaseLayer name="Luchtfoto">
                            <TileLayer
                                attribution='&copy; <a href="https://www.nationaalgeoregister.nl/">Nationaal Georegister</a>'
                                url="https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_ortho25/EPSG:3857/{z}/{x}/{y}.jpeg"
                            />
                        </LayersControl.BaseLayer>
                    </LayersControl>
                    <Markers />
                    <Circles />
                    {plot && <Plots />}
                    {observations && <Observations data={observations} />}
                </MapContainer>
            </div>
        </>
    )
}

Location.defaultProps = {
    errors: {},
    id: 0,
    observations: [],
    onSelect: null
}
export default Location
